import axios from "axios";
import { BASE_API_URL } from "../consts";

// ===================================
// V2 API - New Expense System
// Supplier Custom Value Interfaces
// ===================================

export interface SupplierCustomValue {
  _id: string;
  clientId: string;
  categoryId?: string;
  supplierId: string;
  supplierName: string;
  locationId: string;
  month: number;
  year: number;
  invoiceDate: string; // ISO date string
  dueDate?: string; // ISO date string - when invoice is due
  description: string;
  amount: number;
  currency: "RSD" | "EUR";
  paymentStatus: "paid" | "unpaid" | "partial";
  partialPayment: {
    paidAmount: number;
    remainingAmount: number;
  };
  // "fixed" = auto-generated monthly recurring; "custom" = one-off invoice
  type?: "fixed" | "custom";
  autoGenerated?: boolean;
  // true when the source supplier was frozen after this record was created
  supplierDeleted?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSupplierCustomValueParams {
  clientId: string;
  supplierId: string;
  supplierName: string;
  locationId: string;
  month: number;
  year: number;
  invoiceDate: string;
  dueDate?: string;
  description?: string;
  amount: number;
  currency?: "RSD" | "EUR";
  paymentStatus?: "paid" | "unpaid" | "partial";
  partialPayment?: {
    paidAmount: number;
    remainingAmount: number;
  };
}

export interface GetSupplierCustomValuesParams {
  clientId: string;
  locationId?: string;
  month?: number;
  year?: number;
  months?: string; // Format: "YYYY-MM,YYYY-MM" for multi-month queries
  supplierId?: string;
}

// ===================================
// Supplier Default Value Interfaces (V2 Optimized)
// ===================================

export interface SupplierDefaultValue {
  categoryId: string;
  categoryName: string;
  supplierId: string;
  supplierName: string;
  locationId: string | null;
  defaultValue: number;
  startsAt: string; // ISO date string
  month?: string; // Format: YYYY-MM (added for multi-month periods)
}

export interface GetSupplierDefaultValuesParams {
  clientId: string;
  month?: string; // Format: YYYY-MM (backward compatibility)
  months?: string; // Format: "YYYY-MM,YYYY-MM" for multi-month queries
  locationId?: string;
  categoryId?: string;
  supplierId?: string;
}

export interface GetSingleDefaultValueParams {
  clientId: string;
  supplierId: string;
  month: string; // Format: YYYY-MM
  locationId?: string;
}

// ===================================
// Daily Revenue Supplier Expense Interfaces
// ===================================

export interface DailyRevenueSupplierExpense {
  _id: string;
  clientId: string;
  dailyRevenueId: string;
  locationId: string;
  categoryId: string;
  supplierId: string;
  supplierName: string;
  amount: number;
  paymentStatus: "paid" | "unpaid" | "partial";
  partialPayment: {
    paidAmount: number;
    remainingAmount: number;
  };
  date: string;
  description: string;
  other: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDailyRevenueSupplierExpenseParams {
  clientId: string;
  dailyRevenueId: string;
  locationId: string;
  categoryId: string;
  supplierId: string;
  supplierName: string;
  amount: number;
  paymentStatus?: "paid" | "unpaid" | "partial";
  partialPayment?: {
    paidAmount: number;
    remainingAmount: number;
  };
  date: string;
  description?: string;
  other?: boolean;
}

export interface GetDailyRevenueSupplierExpensesParams {
  clientId: string;
  locationId?: string;
  dailyRevenueId?: string;
  supplierId?: string;
  startDate?: string;
  endDate?: string;
  months?: string; // Format: "YYYY-MM,YYYY-MM" for multi-month queries
}

export interface SupplierExpenseAggregation {
  _id: {
    supplierId: string;
    supplierName: string;
    categoryId: string;
  };
  totalAmount: number;
  count: number;
  expenses: DailyRevenueSupplierExpense[];
}

// ===================================
// Supplier Custom Value API Functions
// ===================================

/**
 * Kreira novu custom vrednost za dobavljača
 */
export const createSupplierCustomValue = async (
  params: CreateSupplierCustomValueParams,
): Promise<{ message: string; data: SupplierCustomValue }> => {
  const response = await axios.post(
    `${BASE_API_URL}/v2/supplier-custom-values`,
    params,
  );
  return response.data;
};

/**
 * Dohvata sve custom vrednosti sa filterima
 */
export const getSupplierCustomValues = async (
  params: GetSupplierCustomValuesParams,
): Promise<{ data: SupplierCustomValue[] }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/supplier-custom-values`,
    {
      params,
    },
  );
  return response.data;
};

/**
 * Dohvata jednu custom vrednost po ID-u
 */
export const getSupplierCustomValueById = async (
  id: string,
): Promise<{ data: SupplierCustomValue }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/supplier-custom-values/${id}`,
  );
  return response.data;
};

/**
 * Ažurira postojeću custom vrednost
 */
export const updateSupplierCustomValue = async (
  id: string,
  updates: Partial<CreateSupplierCustomValueParams>,
): Promise<{ message: string; data: SupplierCustomValue }> => {
  const response = await axios.put(
    `${BASE_API_URL}/v2/supplier-custom-values/${id}`,
    updates,
  );
  return response.data;
};

/**
 * Briše custom vrednost
 */
export const deleteSupplierCustomValue = async (
  id: string,
): Promise<{ message: string }> => {
  const response = await axios.delete(
    `${BASE_API_URL}/v2/supplier-custom-values/${id}`,
  );
  return response.data;
};

/**
 * Briše više custom vrednosti odjednom
 */
export const bulkDeleteSupplierCustomValues = async (
  ids: string[],
): Promise<{ message: string; deletedCount: number }> => {
  const response = await axios.delete(
    `${BASE_API_URL}/v2/supplier-custom-values/bulk-delete`,
    {
      data: { ids },
    },
  );
  return response.data;
};

/**
 * Ažurira payment status za custom vrednost (brza akcija)
 */
export const updateCustomValuePaymentStatus = async (
  id: string,
  paymentStatus: "paid" | "unpaid" | "partial",
  paidAmount?: number,
): Promise<{ message: string; data: SupplierCustomValue }> => {
  const response = await axios.patch(
    `${BASE_API_URL}/v2/supplier-custom-values/${id}/payment-status`,
    { paymentStatus, paidAmount },
  );
  return response.data;
};

// ===================================
// Daily Revenue Supplier Expense API Functions
// ===================================

/**
 * Kreira novi rashod preko dobavljača u daily revenue
 */
export const createDailyRevenueSupplierExpense = async (
  params: CreateDailyRevenueSupplierExpenseParams,
): Promise<{ message: string; data: DailyRevenueSupplierExpense }> => {
  const response = await axios.post(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses`,
    params,
  );
  return response.data;
};

/**
 * Dohvata sve rashode preko dobavljača sa filterima
 */
export const getDailyRevenueSupplierExpenses = async (
  params: GetDailyRevenueSupplierExpensesParams,
): Promise<{ data: DailyRevenueSupplierExpense[] }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses`,
    {
      params,
    },
  );
  return response.data;
};

/**
 * Dohvata rashode grupisane po dobavljaču
 */
export const getDailyRevenueSupplierExpensesBySupplier = async (
  params: GetDailyRevenueSupplierExpensesParams,
): Promise<{ data: SupplierExpenseAggregation[] }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/by-supplier`,
    {
      params,
    },
  );
  return response.data;
};

/**
 * Dohvata jedan rashod po ID-u
 */
export const getDailyRevenueSupplierExpenseById = async (
  id: string,
): Promise<{ data: DailyRevenueSupplierExpense }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/${id}`,
  );
  return response.data;
};

/**
 * Ažurira postojeći rashod
 */
export const updateDailyRevenueSupplierExpense = async (
  id: string,
  updates: Partial<CreateDailyRevenueSupplierExpenseParams>,
): Promise<{ message: string; data: DailyRevenueSupplierExpense }> => {
  const response = await axios.put(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/${id}`,
    updates,
  );
  return response.data;
};

/**
 * Briše rashod
 */
export const deleteDailyRevenueSupplierExpense = async (
  id: string,
): Promise<{ message: string }> => {
  const response = await axios.delete(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/${id}`,
  );
  return response.data;
};

/**
 * Briše više rashoda odjednom
 */
export const bulkDeleteDailyRevenueSupplierExpenses = async (
  ids: string[],
): Promise<{ message: string; deletedCount: number }> => {
  const response = await axios.delete(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/bulk-delete`,
    {
      data: { ids },
    },
  );
  return response.data;
};

/**
 * Briše sve rashode za određeni daily revenue
 */
export const deleteDailyRevenueSupplierExpensesByDailyRevenue = async (
  dailyRevenueId: string,
): Promise<{ message: string; deletedCount: number }> => {
  const response = await axios.delete(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/by-daily-revenue/${dailyRevenueId}`,
  );
  return response.data;
};

/**
 * Ažurira payment status za daily revenue supplier expense (brza akcija)
 */
export const updateDailyRevenueExpensePaymentStatus = async (
  id: string,
  paymentStatus: "paid" | "unpaid" | "partial",
  paidAmount?: number,
): Promise<{ message: string; data: DailyRevenueSupplierExpense }> => {
  const response = await axios.patch(
    `${BASE_API_URL}/v2/daily-revenue-supplier-expenses/${id}/payment-status`,
    { paymentStatus, paidAmount },
  );
  return response.data;
};

// ===================================
// Supplier Default Values API Functions (V2 Optimized)
// ===================================

/**
 * Dohvata default vrednosti dobavljača za dati mesec
 * Optimizovano - bez V1 legacy koda (customEntries, dailyRevenue agregacija)
 */
export const getSupplierDefaultValues = async (
  params: GetSupplierDefaultValuesParams,
): Promise<{
  data: SupplierDefaultValue[];
  count: number;
  filters: GetSupplierDefaultValuesParams;
}> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/supplier-default-values`,
    {
      params,
    },
  );
  return response.data;
};

/**
 * Dohvata default vrednost za specifičan dobavljač/lokacija/mesec kombinaciju
 */
export const getSingleSupplierDefaultValue = async (
  params: GetSingleDefaultValueParams,
): Promise<{ data: SupplierDefaultValue | null }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/supplier-default-values/single`,
    {
      params,
    },
  );
  return response.data;
};

// ===================================
// Monthly Fixed Expense Generation
// ===================================

export interface MonthlyExpenseGenerationRecord {
  _id: string;
  clientId: string;
  month: number;
  year: number;
  generatedAt: string;
  generatedBy: string | null;
  expenseCount: number;
  status: "generated" | "failed" | "partial";
}

/** Keyed by "YYYY-MM" */
export type MonthlyGenerationStatusMap = Record<
  string,
  MonthlyExpenseGenerationRecord
>;

/**
 * Generates fixed SupplierCustomValue records for all active suppliers
 * for the given client + month + year.
 * Returns 409 if already generated.
 */
export const generateMonthlyFixedExpenses = async (params: {
  clientId: string;
  month: number;
  year: number;
}): Promise<{
  message: string;
  expenseCount: number;
  generation: MonthlyExpenseGenerationRecord;
}> => {
  const response = await axios.post(
    `${BASE_API_URL}/v2/monthly-fixed-expenses/generate`,
    params,
  );
  return response.data;
};

/**
 * Returns the generation status for each requested month.
 * Months format: "YYYY-MM,YYYY-MM"
 */
export const getMonthlyExpenseGenerationStatus = async (params: {
  clientId: string;
  months: string;
}): Promise<{ data: MonthlyGenerationStatusMap }> => {
  const response = await axios.get(
    `${BASE_API_URL}/v2/monthly-fixed-expenses/status`,
    { params },
  );
  return response.data;
};
